import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Gift, Users, Share2, Truck, CheckCircle, Copy, 
  MessageCircle, ArrowRight, Star, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CONTACT_INFO } from '@/data/mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Referral = () => {
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [referralCode, setReferralCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ uses_count: 0, credits_earned: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/referrals/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        setReferralCode(data.code);
        setStats({
          uses_count: data.uses_count || 0,
          credits_earned: data.credits_earned || 0
        });
        toast.success('Your referral code is ready!');
      } else {
        toast.error(data.detail || 'Failed to create referral code');
      }
    } catch (error) {

      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Code copied to clipboard!');
  };

  const shareViaWhatsApp = () => {
    const message = `Hey! Use my Rocket Van Express referral code ${referralCode} to get a discount on your first delivery or mini-move. They're great for student moves! https://rocketvan.co.za/quote?ref=${referralCode}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const howItWorks = [
    {
      step: 1,
      title: 'Get Your Code',
      description: 'Sign up above to get your unique referral code',
      icon: Gift
    },
    {
      step: 2,
      title: 'Share With Friends',
      description: 'Send your code to friends, classmates, or family',
      icon: Share2
    },
    {
      step: 3,
      title: 'They Get a Discount',
      description: 'Your referrals get R50 off their first booking',
      icon: Star
    },
    {
      step: 4,
      title: 'You Earn Free Deliveries',
      description: 'When they book, you earn a free delivery credit',
      icon: Truck
    }
  ];

  const rewards = [
    { referrals: 1, reward: '1 Free Local Delivery', icon: '📦' },
    { referrals: 3, reward: '1 Free Mini-Move Discount (R200)', icon: '🚛' },
    { referrals: 5, reward: 'VIP Priority Booking', icon: '⭐' },
    { referrals: 10, reward: 'Free Mini-Move (up to R500)', icon: '🎉' }
  ];

  return (
    <div className="min-h-screen" data-testid="referral-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-[#fb6402]/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="h-4 w-4 mr-2 text-[#fb6402]" />
              <span className="text-sm font-medium">Student Referral Program</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Share Rocket Van Express, Earn Free Deliveries
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Refer your friends and earn free delivery credits. Perfect for students moving in and out of res!
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Sign Up / Code Display */}
              <Card className="border-2 border-[#039fff]">
                <CardContent className="p-6 md:p-8">
                  {!referralCode ? (
                    <>
                      <div className="flex items-center mb-6">
                        <Gift className="h-8 w-8 text-[#fb6402] mr-3" />
                        <h2 className="text-2xl font-bold text-[#001A47]">Get Your Code</h2>
                      </div>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="name">Your Name</Label>
                          <Input
                            id="name"
                            placeholder="Full name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="your@email.com"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="mobile">Mobile Number</Label>
                          <Input
                            id="mobile"
                            type="tel"
                            placeholder="+27"
                            value={formData.mobile}
                            onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                            required
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-[#fb6402] hover:bg-[#e55a02]"
                          disabled={isLoading}
                        >
                          {isLoading ? 'Creating...' : 'Get My Referral Code'}
                        </Button>
                      </form>
                    </>
                  ) : (
                    <>
                      <div className="text-center">
                        <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#001A47] mb-2">Your Referral Code</h2>
                        <p className="text-gray-600 mb-6">Share this code with friends to earn rewards</p>
                        
                        <div className="bg-[#001A47] rounded-lg p-4 mb-6">
                          <p className="text-3xl font-mono font-bold text-white tracking-wider">
                            {referralCode}
                          </p>
                        </div>

                        <div className="flex gap-3 mb-6">
                          <Button 
                            onClick={copyToClipboard}
                            variant="outline"
                            className="flex-1"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Code
                          </Button>
                          <Button 
                            onClick={shareViaWhatsApp}
                            className="flex-1 bg-[#25D366] hover:bg-[#22c55e]"
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Share
                          </Button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-[#039fff]">{stats.uses_count}</p>
                            <p className="text-sm text-gray-600">Friends Referred</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-[#fb6402]">{stats.credits_earned}</p>
                            <p className="text-sm text-gray-600">Free Deliveries Earned</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Rewards Tiers */}
              <div>
                <h3 className="text-xl font-bold text-[#001A47] mb-4 flex items-center">
                  <Star className="h-5 w-5 text-[#fb6402] mr-2" />
                  Reward Tiers
                </h3>
                <div className="space-y-3">
                  {rewards.map((tier, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 flex items-center">
                        <span className="text-2xl mr-4">{tier.icon}</span>
                        <div className="flex-1">
                          <p className="font-medium text-[#001A47]">{tier.reward}</p>
                          <p className="text-sm text-gray-500">{tier.referrals} referral{tier.referrals > 1 ? 's' : ''}</p>
                        </div>
                        {stats.uses_count >= tier.referrals && (
                          <Badge className="bg-green-100 text-green-700">Unlocked</Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">How It Works</h2>
            <p className="text-gray-600">Earning free deliveries is easy</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {howItWorks.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="text-center">
                  <div className="bg-[#fb6402] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <Icon className="h-8 w-8 text-[#039fff] mx-auto mb-3" />
                    <h3 className="font-bold text-[#001A47] mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#001A47] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Earning?</h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Get your referral code now and start sharing with friends. Every referral brings you closer to free deliveries!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#fb6402] hover:bg-[#e55a02]"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Get My Code
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link to="/partners">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47]">
                Student Partner Pages
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Referral;
